import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { IconHolder } from '@repo/studio-components-icon-holder'
import { LoadedImage } from '@repo/studio-components-loaded-image'
import { Button } from '@repo/studio-design-system-ui-button'
import { MessageChatCircleIcon } from '@repo/studio-design-system-ui-icons/communication/message-chat-circle'
import { Image01Icon } from '@repo/studio-design-system-ui-icons/images/image-01'
import { ImagesIcon } from '@repo/studio-design-system-ui-icons/images/images'
import { MusicNote01Icon } from '@repo/studio-design-system-ui-icons/media-devices/music-note-01'
import { VideoRecorderIcon } from '@repo/studio-design-system-ui-icons/media-devices/video-recorder'
import {
  ASSET_DRAG_SOURCE_CLASS,
  useAssetDrag,
} from '@repo/studio-components-asset-drag/use-asset-drag'
import { ScrollShadow } from '@repo/studio-design-system-ui-scroll-shadow'
import { SectionTabs } from '@repo/studio-design-system-ui-tab'
import { TagStatus } from '@repo/studio-design-system-ui-tag'
import type { TagStatusType } from '@repo/studio-design-system-ui-tag'
import { ButtonTooltip } from '@repo/studio-design-system-ui-tooltip'
import { cfAssetFromMedia } from '../../asset-drag/aida-asset'
import { aidaChatKeys } from '../../constants/query-keys'
import { useAidaChat } from '../../context/aida-chat-context'
import { useAidaConversationMedia } from '../../hooks/use-aida-conversation-media'
import { closeAidaMediaLightbox, openAidaMediaPlaylist } from '../lightbox/store'
import type { AidaMediaKind, AidaMediaStatus } from '../../types'
import type { AidaConversationMedia } from '../../utils/conversation-media'
import { mediaLightboxSelection, toAidaLightboxItem } from '../../utils/media-lightbox'
import type { MessagesData } from '../../utils/messages-cache'
import { tokenLabel } from '../../utils/tokens'
import { threadScrollShadowClass } from '../../utils/presentation-layout'

type MediaPane = 'chat' | 'media'

const kindIcons = {
  image: Image01Icon,
  video: VideoRecorderIcon,
  audio: MusicNote01Icon,
} satisfies Record<AidaMediaKind, React.ComponentType>

const kindLabelKey = {
  image: 'studio.components.aida.media_kind_image',
  video: 'studio.components.aida.media_kind_video',
  audio: 'studio.components.aida.media_kind_audio',
} as const

const statusLabelKey = {
  ready: 'studio.components.aida.media_done',
  failed: 'studio.components.aida.media_failed',
  generating: 'studio.components.aida.media_generating',
} as const

const statusTag = {
  ready: 'success',
  failed: 'error',
  generating: 'warning',
} as const satisfies Record<AidaMediaStatus, TagStatusType>

const mediaScrollerClass = 'h-full overflow-y-auto overflow-x-clip scroller-xs px-3 pb-4 pt-2'
const mediaGridClass =
  'grid grid-cols-2 gap-2 @md:grid-cols-3 @xl:grid-cols-4 @3xl:grid-cols-6 @5xl:grid-cols-8'

interface AidaMediaPaneValue {
  pane: MediaPane
  setPane: (pane: MediaPane) => void
  items: AidaConversationMedia[]
  isLoading: boolean
  highlightMessageId: string | null
  clearHighlight: () => void
  showInChat: (messageId: string | undefined) => void
}

const AidaMediaPaneContext = React.createContext<AidaMediaPaneValue | null>(null)

export function useAidaMediaPane() {
  const pane = React.useContext(AidaMediaPaneContext)
  if (!pane) {
    throw new Error('useAidaMediaPane must be used within AidaChatLayout')
  }
  return pane
}

export function useAidaMediaPaneState(): AidaMediaPaneValue {
  const { activeConversationId, fetchOlderMessages } = useAidaChat()
  const { items, isLoading } = useAidaConversationMedia(activeConversationId)
  const queryClient = useQueryClient()
  const [paneState, setPaneState] = React.useState<{
    conversationId: string | null
    pane: MediaPane
    highlightMessageId: string | null
  }>({ conversationId: activeConversationId, pane: 'chat', highlightMessageId: null })
  const current = paneState.conversationId === activeConversationId
  const hasMedia = items.length > 0
  const pane = current && hasMedia ? paneState.pane : 'chat'
  const highlightMessageId = current ? paneState.highlightMessageId : null

  const setPane = React.useCallback(
    (next: MediaPane) => {
      setPaneState(state => ({
        conversationId: activeConversationId,
        pane: next,
        highlightMessageId:
          state.conversationId === activeConversationId ? state.highlightMessageId : null,
      }))
    },
    [activeConversationId],
  )

  React.useEffect(() => {
    if (!current || isLoading || hasMedia || paneState.pane !== 'media') {
      return
    }
    setPane('chat')
  }, [current, hasMedia, isLoading, paneState.pane, setPane])

  const clearHighlight = React.useCallback((onlyMessageId?: string) => {
    setPaneState(state => {
      if (state.highlightMessageId === null) {
        return state
      }
      if (onlyMessageId && state.highlightMessageId !== onlyMessageId) {
        return state
      }
      return { ...state, highlightMessageId: null }
    })
  }, [])

  const showInChat = React.useCallback(
    (messageId: string | undefined) => {
      setPaneState({
        conversationId: activeConversationId,
        pane: 'chat',
        highlightMessageId: messageId ?? null,
      })
      if (!messageId || !activeConversationId) {
        return
      }
      void loadUntilMessage(queryClient, activeConversationId, messageId, fetchOlderMessages).then(
        found => {
          if (!found) {
            clearHighlight(messageId)
          }
        },
      )
    },
    [activeConversationId, clearHighlight, fetchOlderMessages, queryClient],
  )

  return { pane, setPane, items, isLoading, highlightMessageId, clearHighlight, showInChat }
}

export function AidaMediaPaneProvider({
  value,
  children,
}: {
  value: AidaMediaPaneValue
  children: React.ReactNode
}) {
  return <AidaMediaPaneContext value={value}>{children}</AidaMediaPaneContext>
}

export function MediaTabBar({ className }: { className?: string }) {
  const { pane, setPane, items } = useAidaMediaPane()
  const { t } = useTranslation('components')

  return (
    <SectionTabs.Root
      className={className}
      value={pane}
      onValueChange={value => {
        if (value === 'chat' || value === 'media') {
          setPane(value)
        }
      }}
    >
      <SectionTabs.List>
        <SectionTabs.Tab
          value="chat"
          size="small"
          variant="subtle"
          icon={<MessageChatCircleIcon />}
        >
          {t('studio.components.aida.chat_tab')}
        </SectionTabs.Tab>
        <SectionTabs.Tab value="media" size="small" variant="subtle" icon={<ImagesIcon />}>
          {t('studio.components.aida.media_tab', { count: items.length })}
        </SectionTabs.Tab>
      </SectionTabs.List>
    </SectionTabs.Root>
  )
}

export function MediaGrid() {
  const { items, isLoading } = useAidaMediaPane()
  const { t } = useTranslation('components')

  if (items.length === 0) {
    if (isLoading) {
      return <div className="min-h-0 flex-1" />
    }
    return (
      <p className="grid min-h-0 flex-1 place-items-center px-4 text-center studio-typography-14 text-theme_semantic_surface_ui_foreground-color-subtle">
        {t('studio.components.aida.media_empty')}
      </p>
    )
  }

  return (
    <ScrollShadow className={threadScrollShadowClass}>
      <div className={mediaScrollerClass}>
        <ul className={mediaGridClass}>
          {items.map(item => (
            <li key={item.id}>
              <MediaTile item={item} />
            </li>
          ))}
        </ul>
      </div>
    </ScrollShadow>
  )
}

function MediaTile({ item }: { item: AidaConversationMedia }) {
  const { items, setPane, showInChat } = useAidaMediaPane()
  const chat = useAidaChat()
  const { t } = useTranslation('components')
  const drag = useAssetDrag(cfAssetFromMedia(item))
  const KindIcon = kindIcons[item.kind]
  const src = tileSrc(item)
  const showInChatLabel = t('studio.components.aida.showInChat')

  return (
    <div
      draggable={drag.draggable}
      onDragStart={drag.onDragStart}
      onDragEnd={drag.onDragEnd}
      className={`relative aspect-square w-full overflow-hidden rounded-2 bg-theme_semantic_surface_container_subtle ${
        drag.draggable ? ASSET_DRAG_SOURCE_CLASS : ''
      }`}
    >
      <button
        type="button"
        className="absolute inset-0"
        onClick={() => {
          const selection = mediaLightboxSelection(items, item.id)
          openAidaMediaPlaylist(
            selection.playlist.map(entry =>
              toAidaLightboxItem(entry, showInChat, {
                messages: chat.messages,
                onEditPrompt: draft => {
                  chat.setDraft(draft)
                  setPane('chat')
                  closeAidaMediaLightbox({ suppressAidaClose: true })
                },
              }),
            ),
            selection.index,
          )
        }}
      >
        {src ? (
          <LoadedImage
            src={src}
            alt={t(kindLabelKey[item.kind])}
            className="size-full"
            imageVariant="cover"
          />
        ) : (
          <span className="grid size-full place-items-center text-theme_semantic_surface_ui_foreground-color-subtle">
            <IconHolder size="large">
              <KindIcon />
            </IconHolder>
          </span>
        )}
        <span className="pointer-events-none absolute left-1.5 top-1.5 rounded-2 bg-theme_semantic_surface_primary-background-elevated p-1">
          <IconHolder size="small">
            <KindIcon />
          </IconHolder>
        </span>
        <span className="pointer-events-none absolute bottom-1.5 left-1.5">
          <TagStatus statusType={statusTag[item.status]} visualWeight="regular" size="sm">
            {t(statusLabelKey[item.status])}
          </TagStatus>
        </span>
        {item.promptRef ? (
          <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded-2 bg-theme_semantic_surface_primary-background-elevated px-1.5 py-0.5 studio-typography-12">
            {tokenLabel(item.promptRef)}
          </span>
        ) : null}
      </button>
      <ButtonTooltip value={showInChatLabel} side="left">
        <Button
          type="button"
          iconOnly
          size="xs"
          variant="ghost"
          aria-label={showInChatLabel}
          className="absolute right-1.5 top-1.5"
          onClick={() => {
            showInChat(item.messageId)
          }}
        >
          <MessageChatCircleIcon />
        </Button>
      </ButtonTooltip>
    </div>
  )
}

function tileSrc(item: AidaConversationMedia) {
  if (item.kind === 'video') {
    return item.posterUrl || item.thumbnailUrl
  }
  if (item.kind === 'audio') {
    return item.thumbnailUrl
  }
  return item.thumbnailUrl || item.url
}

async function loadUntilMessage(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  messageId: string,
  fetchOlderMessages: () => Promise<unknown>,
) {
  const key = aidaChatKeys.messages(conversationId)
  let data = queryClient.getQueryData<MessagesData>(key)
  let pages = data?.pages.length ?? 0
  while (!cacheHasMessage(data, messageId) && hasOlderPage(data)) {
    try {
      await fetchOlderMessages()
    } catch {
      return false
    }
    data = queryClient.getQueryData<MessagesData>(key)
    const next = data?.pages.length ?? 0
    if (next <= pages) {
      return false
    }
    pages = next
  }
  return cacheHasMessage(data, messageId)
}

function cacheHasMessage(data: MessagesData | undefined, messageId: string) {
  return data?.pages.some(page => page.messages.some(message => message.id === messageId)) ?? false
}

function hasOlderPage(data: MessagesData | undefined) {
  const next = data?.pages.at(-1)?.pagination?.next?.value
  return typeof next === 'string' && next.length > 0
}
